<template>
  <main class="invitation-page">
    <section class="invitation-shell" aria-labelledby="invitation-title">
      <div class="invitation-brand">
        <span>{{ productName }}</span>
        <small>契約企業管理者への招待</small>
      </div>

      <div v-if="loading" class="invitation-state" role="status" aria-live="polite">
        <CSpinner color="primary"/>
        <h1 id="invitation-title">招待内容を確認しています</h1>
        <p>しばらくお待ちください。</p>
      </div>

      <div v-else-if="completed" class="invitation-state invitation-state--success" role="status" aria-live="polite">
        <span class="state-icon" aria-hidden="true">✓</span>
        <h1 id="invitation-title">招待の受諾が完了しました</h1>
        <p>{{ responseMessage || '設定したパスワードで' + productName + 'へログインできます。' }}</p>
        <CButton color="primary" class="primary-action" @click="goToLogin">
          ログイン画面へ
        </CButton>
      </div>

      <div v-else-if="errorMessage" class="invitation-state invitation-state--error" role="alert">
        <span class="state-icon" aria-hidden="true">!</span>
        <h1 id="invitation-title">招待を確認できませんでした</h1>
        <p>{{ errorMessage }}</p>
        <div class="state-actions">
          <CButton color="primary" variant="outline" @click="validateInvitation">
            もう一度確認する
          </CButton>
          <CButton color="secondary" @click="goToLogin">
            ログイン画面へ
          </CButton>
        </div>
      </div>

      <template v-else>
        <header class="invitation-header">
          <p class="eyebrow">TENANT ADMIN INVITATION</p>
          <h1 id="invitation-title">契約企業管理者として参加する</h1>
          <p v-if="existingUser">
            現在の契約企業管理者から、自社の追加管理者として招待されています。
            登録済みの{{ productName }}アカウントでログインし、現在のパスワードで本人確認を行ってください。
          </p>
          <p v-else>
            PREIKA運営管理者または現在の契約企業管理者から、次の契約企業の管理者として招待されています。
            この権限は自社内では最上位ですが、他社や{{ productName }}全体の設定は管理しません。
          </p>
        </header>

        <dl class="invitation-summary">
          <div>
            <dt>契約企業</dt>
            <dd>{{ invitation.companyName || '未設定' }}</dd>
          </div>
          <div>
            <dt>招待された方</dt>
            <dd>{{ invitation.invitedName || '未設定' }}</dd>
          </div>
          <div>
            <dt>メールアドレス</dt>
            <dd>{{ invitation.maskedEmail || '確認できません' }}</dd>
          </div>
          <div>
            <dt>受諾期限</dt>
            <dd>{{ formatDateTime(invitation.expiresAt) }}</dd>
          </div>
        </dl>

        <CAlert color="info">
          契約企業管理者は、自社の利用者・役割・権限・会社設定を管理します。
          PREIKA運営側の契約・プラン・他社環境を操作する権限ではありません。
        </CAlert>

        <!--
          受け取り方は**サーバが返すフラグ**で決める。文言で判断させると、
          言い回しを直した日に画面が壊れる。
        -->
        <section v-if="passkeyOffered" class="passkey-offer">
          <div class="form-section-heading">
            <h2>パスキーで受け取る</h2>
            <p v-if="passwordOffered">
              この端末の指紋・顔認証・PINで、パスワードを決めずに始められます。
            </p>
            <p v-else>
              この会社ではパスキーでログインします。
              この端末の指紋・顔認証・PINを使って受け取ってください。
            </p>
          </div>

          <CAlert v-if="!passkeySupported" color="warning">
            このブラウザはパスキーに対応していません。
            対応しているブラウザで招待リンクを開き直してください。
          </CAlert>

          <CInput
            label="この端末の名前（任意）"
            maxlength="40"
            v-model="passkeyLabel"
            description="あとで一覧から見分けるための名前です。"
            :disabled="registeringPasskey || !passkeySupported"
          />

          <CAlert v-if="passkeyError" color="danger">
            {{ passkeyError }}
          </CAlert>

          <CButton
            type="button"
            color="primary"
            class="primary-action"
            :disabled="registeringPasskey || !passkeySupported"
            @click="acceptWithPasskey"
          >
            {{ registeringPasskey ? '受け取っています' : 'パスキーで受け取る' }}
          </CButton>
        </section>

        <div v-if="passkeyOffered && passwordOffered" class="way-divider">
          <span>または</span>
        </div>

        <CAlert v-if="noWayToAccept" color="warning">
          この会社では、いまこの招待を受け取れる方法がありません。
          会社の管理者へお問い合わせください。
        </CAlert>

        <form
          v-if="passwordOffered"
          class="password-form"
          @submit.prevent="acceptInvitation"
          novalidate
        >
          <div class="form-section-heading">
            <h2>{{ existingUser ? '登録済みアカウントで本人確認' : '本人用のパスワードを設定' }}</h2>
            <p v-if="existingUser">
              招待されたアカウントでログインした状態で、現在のパスワードを入力してください。
            </p>
            <p v-else>
              パスワードはメールには記載されず、PREIKA運営担当者にも共有されません。
            </p>
          </div>

          <template v-if="existingUser">
            <CAlert v-if="loginRequired" color="warning">
              受諾にはログインが必要です。招待されたメールアドレスのアカウントでログインしてください。
            </CAlert>
            <CInput
              :type="showPassword ? 'text' : 'password'"
              label="現在のパスワード"
              autocomplete="current-password"
              maxlength="64"
              v-model="currentPassword"
            />
          </template>
          <template v-else>
            <CInput
              :type="showPassword ? 'text' : 'password'"
              label="新しいパスワード"
              autocomplete="new-password"
              minlength="12"
              maxlength="64"
              v-model="password"
              description="12文字以上64文字以内で設定してください。"
              :is-valid="passwordState"
            />
            <CInput
              :type="showPassword ? 'text' : 'password'"
              label="新しいパスワード（確認）"
              autocomplete="new-password"
              minlength="12"
              maxlength="64"
              v-model="passwordConfirmation"
              :is-valid="confirmationState"
            />
          </template>

          <label class="show-password-option">
            <input type="checkbox" v-model="showPassword">
            <span>入力したパスワードを表示する</span>
          </label>

          <CAlert v-if="formError" color="danger" class="mt-3">
            {{ formError }}
          </CAlert>

          <div class="security-notes">
            <strong>受諾前に確認してください</strong>
            <ul>
              <li>心当たりがない場合は、この画面で設定せず現在の契約企業管理者へ連絡してください。</li>
              <li>招待リンクは一度だけ利用でき、受諾期限を過ぎると無効になります。</li>
              <li>メールリンクだけでは管理者権限は付与されません。本人確認完了時に有効になります。</li>
            </ul>
          </div>

          <CButton
            v-if="existingUser && loginRequired"
            type="button"
            color="primary"
            class="primary-action"
            @click="goToLoginForAcceptance"
          >
            ログインしてこの画面へ戻る
          </CButton>
          <CButton
            v-else
            type="submit"
            color="primary"
            class="primary-action"
            :disabled="submitting || !canSubmit"
          >
            {{ submitting ? '受諾しています' : (existingUser ? '再認証して管理者招待を受諾' : '招待を受諾してアカウントを有効化') }}
          </CButton>
        </form>

      </template>
    </section>
  </main>
</template>

<script>
import api from '@/services/accountInvitation'
import { PRODUCT_NAME } from '../../common/productName'
import { acceptInvitationWithPasskey, isPasskeySupported } from '@/services/passkey'

export default {
  name: 'AccountInvitation',
  data () {
    return {
      loading: true,
      submitting: false,
      completed: false,
      invitation: {},
      password: '',
      passwordConfirmation: '',
      currentPassword: '',
      loginRequired: false,
      showPassword: false,
      errorMessage: '',
      formError: '',
      responseMessage: '',
      passkeyLabel: '',
      passkeyError: '',
      registeringPasskey: false,
      passkeySupported: isPasskeySupported()
    }
  },
  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    token () {
      return String((this.$route.query && this.$route.query.token) || '').trim()
    },
    existingUser () {
      return String(this.invitation.invitationType || this.invitation.invitation_type || '') === 'TENANT_OWNER_EXISTING'
    },
    passwordState () {
      if (!this.password) return null
      return this.password.length >= 12 && this.password.length <= 64
    },
    confirmationState () {
      if (!this.passwordConfirmation) return null
      return this.passwordConfirmation === this.password && this.passwordState === true
    },
    canSubmit () {
      if (!this.token) return false
      if (this.existingUser) return Boolean(this.currentPassword) && !this.loginRequired
      return this.passwordState === true && this.confirmationState === true
    },
    /*
     * 受け取り方は**サーバのフラグ**で決める。
     *
     * フラグが来ない相手（古いサーバ、追加管理者の招待）ではパスワードだけを
     * 出す。**出さなくなるより、いまの動きのままのほうが安全。**
     */
    passwordOffered () {
      if (this.existingUser) return true
      /*
       * **「フラグが来ていない」と「両方 false」は違う。**
       *
       * 前者は古いサーバや追加管理者の招待で、いままでどおり出す。
       * 後者はサーバが「いま受け取れる方法が無い」と言っている。
       * 一緒くたにすると、受け取れないのに欄だけ出る。
       */
      if (typeof this.invitation.passwordAccepted !== 'boolean') return true
      return this.invitation.passwordAccepted
    },
    passkeyOffered () {
      if (this.existingUser) return false
      return this.invitation.passkeyAccepted === true
    },
    noWayToAccept () {
      return !this.passwordOffered && !this.passkeyOffered
    }
  },
  created () {
    this.validateInvitation()
  },
  methods: {
    async validateInvitation () {
      this.loading = true
      this.errorMessage = ''
      this.formError = ''
      if (!this.token) {
        this.loading = false
        this.errorMessage = '招待リンクが不完全です。メールに記載されたURLを開き直してください。'
        return
      }
      try {
        const response = await api.validate(this.token)
        this.invitation = response || {}
        const status = String(this.invitation.status || '')
        if (status === 'ACCEPTED') {
          this.responseMessage = 'この招待は既に受諾済みです。ログイン画面からログインしてください。'
          this.completed = true
        } else if (status === 'REVOKED') {
          throw new Error('この招待は取り消されています。')
        } else if (status === 'EXPIRED') {
          throw new Error('招待リンクの有効期限が切れています。現在の契約企業管理者へ再送を依頼してください。')
        }
      } catch (error) {
        this.errorMessage = this.resolveError(error, '招待リンクが無効、使用済み、取消済み、または期限切れです。')
      } finally {
        this.loading = false
      }
    },
    async acceptInvitation () {
      this.formError = ''
      if (!this.canSubmit) {
        this.formError = this.existingUser
          ? '現在のパスワードを入力してください。'
          : 'パスワードを12文字以上64文字以内で入力し、確認用と一致させてください。'
        return
      }
      this.submitting = true
      try {
        const response = this.existingUser
          ? await api.acceptExistingOwnerInvitation({
            token: this.token,
            currentPassword: this.currentPassword
          })
          : await api.accept({
            token: this.token,
            password: this.password,
            passwordConfirmation: this.passwordConfirmation
          })
        this.responseMessage = response && response.message
        this.password = ''
        this.passwordConfirmation = ''
        this.currentPassword = ''
        this.loginRequired = false
        this.completed = true
        this.$router.replace({ name: '契約企業管理者招待', query: {} }).catch(() => {})
      } catch (error) {
        if (this.existingUser && error && error.response && error.response.status === 401) {
          this.loginRequired = true
          this.formError = '招待されたアカウントでログインしてください。'
        } else {
          this.formError = this.resolveError(error, '招待を受諾できませんでした。招待の再送を契約企業管理者へ依頼してください。')
        }
        this.currentPassword = ''
      } finally {
        this.submitting = false
      }
    },
    /**
     * パスキーで受け取る。
     *
     * 送るのは招待の token だけ。**誰の鍵として登録するかはサーバが
     * 招待の行から決める。**画面は会社も利用者も知らない。
     */
    async acceptWithPasskey () {
      this.passkeyError = ''
      if (!this.token) {
        this.passkeyError = '招待リンクが不完全です。メールに記載されたURLを開き直してください。'
        return
      }
      this.registeringPasskey = true
      try {
        const response = await acceptInvitationWithPasskey(
          this.token, this.passkeyLabel)
        this.responseMessage = (response && response.message)
          || PRODUCT_NAME + 'へログインできます。'
        this.completed = true
        this.$router.replace({ name: '契約企業管理者招待', query: {} }).catch(() => {})
      } catch (error) {
        /*
         * **押すのをやめただけを、失敗として出さない。**生体認証の画面を
         * 閉じると例外になるが、その人は何も間違えていない。
         */
        if (error && (error.name === 'NotAllowedError'
          || error.message === 'PASSKEY_CANCELLED')) {
          this.passkeyError = '受け取りを中断しました。もう一度お試しください。'
        } else if (error && error.name === 'InvalidStateError') {
          this.passkeyError = 'この端末には、この招待のパスキーが既に登録されています。'
            + 'ログイン画面からパスキーでログインしてください。'
        } else {
          this.passkeyError = this.resolveError(
            error, 'パスキーで受け取れませんでした。時間をおいて、もう一度お試しください。')
        }
      } finally {
        this.registeringPasskey = false
      }
    },
    goToLogin () {
      this.$router.replace({ name: 'Login' }).catch(() => {})
    },
    goToLoginForAcceptance () {
      this.$router.replace({
        name: 'Login',
        query: { redirect: this.$route.fullPath }
      }).catch(() => {})
    },
    formatDateTime (value) {
      if (!value) return '確認できません'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      }).format(date)
    },
    resolveError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data) return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
.invitation-page {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  background:
    radial-gradient(circle at 12% 12%, rgba(37, 132, 197, .18), transparent 34%),
    radial-gradient(circle at 92% 88%, rgba(28, 119, 95, .13), transparent 30%),
    #eef5f8;
}
.invitation-shell {
  width: min(100%, 720px);
  padding: 34px;
  border: 1px solid rgba(112, 145, 162, .25);
  border-radius: 22px;
  background: rgba(255, 255, 255, .98);
  box-shadow: 0 28px 70px rgba(21, 54, 73, .16);
}
.invitation-brand {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4edf2;
  gap: 16px;
}
.invitation-brand span {
  color: #075f9e;
  font-size: 23px;
  font-weight: 900;
  letter-spacing: .08em;
}
.invitation-brand small {
  color: #657b88;
  font-weight: 700;
}
.eyebrow {
  margin: 0 0 8px;
  color: #0872b5;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .14em;
}
.invitation-header h1,
.invitation-state h1 {
  margin: 0;
  color: #17394d;
  font-size: clamp(25px, 5vw, 36px);
  line-height: 1.35;
}
.invitation-header > p:last-child,
.invitation-state p {
  margin: 14px 0 0;
  color: #5d7482;
  line-height: 1.8;
}
.invitation-summary {
  display: grid;
  margin: 26px 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border: 1px solid #dde8ee;
  border-radius: 14px;
  overflow: hidden;
}
.invitation-summary div {
  min-width: 0;
  padding: 16px;
  border-bottom: 1px solid #e5edf2;
}
.invitation-summary div:nth-child(odd) { border-right: 1px solid #e5edf2; }
.invitation-summary div:nth-last-child(-n + 2) { border-bottom: 0; }
.invitation-summary dt {
  color: #6c818d;
  font-size: 10px;
  font-weight: 800;
}
.invitation-summary dd {
  margin: 6px 0 0;
  color: #213f51;
  font-weight: 800;
  overflow-wrap: anywhere;
}
.password-form { margin-top: 26px; }
.form-section-heading h2 {
  margin: 0;
  color: #1c4054;
  font-size: 20px;
}
.form-section-heading p {
  margin: 7px 0 20px;
  color: #697f8b;
  font-size: 12px;
  line-height: 1.7;
}
.show-password-option {
  display: inline-flex;
  align-items: center;
  margin: 4px 0 0;
  color: #506a79;
  font-size: 12px;
  gap: 8px;
}
.security-notes {
  margin: 20px 0;
  padding: 16px;
  border-radius: 12px;
  background: #f4f8fa;
  color: #536b78;
  font-size: 11px;
  line-height: 1.7;
}
.security-notes strong { color: #294b5e; }
.security-notes ul { margin: 8px 0 0; padding-left: 20px; }
.primary-action {
  width: 100%;
  min-height: 48px;
  margin: 0;
  white-space: normal;
}
.invitation-state {
  display: grid;
  justify-items: center;
  padding: 48px 10px;
  text-align: center;
  gap: 14px;
}
.state-icon {
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e6f6ef;
  color: #14805a;
  font-size: 28px;
  font-weight: 900;
}
.invitation-state--error .state-icon {
  background: #fdecec;
  color: #b52e3e;
}
.state-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

@media (max-width: 575.98px) {
  .invitation-page {
    align-items: flex-start;
    padding: 10px;
  }
  .invitation-shell {
    padding: 22px 16px;
    border-radius: 16px;
  }
  .invitation-brand {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
  .invitation-summary {
    grid-template-columns: minmax(0, 1fr);
  }
  .invitation-summary div,
  .invitation-summary div:nth-child(odd),
  .invitation-summary div:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid #e5edf2;
  }
  .invitation-summary div:last-child { border-bottom: 0; }
  .state-actions {
    width: 100%;
    flex-direction: column;
  }
  .state-actions .btn {
    width: 100%;
    min-height: 46px;
    margin: 0;
  }
}

.passkey-offer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* パスワードとパスキーが並ぶときだけ、どちらか一方だと分かるようにする。 */
.way-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1.5rem 0;
  color: #6c757d;
  font-size: 0.875rem;
}

.way-divider::before,
.way-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #dee2e6;
}
</style>
